import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, AlertTriangle, Clock, LogOut } from 'lucide-react';

const PaymentRequiredModal = ({ user, onClose, onLogout }) => {
  const navigate = useNavigate();
  
  if (!user?.payment_required) return null;

  const handleAddPayment = () => {
    navigate('/subscription');
    // Don't close modal - they need to complete payment
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    if (onLogout) onLogout();
    window.location.href = '/';
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="payment-required-modal">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white">Payment Required</h2>
          <p className="text-white/80 mt-2">Your trial period has ended</p>
        </div>
        
        {/* Content */}
        <div className="p-6">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-800 font-medium">Trial Expired</p>
                <p className="text-amber-700 text-sm mt-1">
                  Your 7-day free trial has ended. Add a payment method to continue accessing ClinicalSim AI.
                </p>
              </div>
            </div>
          </div>
          
          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-3 text-slate-700">
              <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                <span className="text-teal-600 font-bold text-sm">✓</span>
              </div>
              <span>Keep your progress and analytics</span>
            </div>
            <div className="flex items-center gap-3 text-slate-700">
              <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                <span className="text-teal-600 font-bold text-sm">✓</span>
              </div>
              <span>Unlimited AI patient simulations</span>
            </div>
            <div className="flex items-center gap-3 text-slate-700">
              <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                <span className="text-teal-600 font-bold text-sm">✓</span>
              </div>
              <span>Full NCLEX practice test access</span>
            </div>
          </div>
          
          <div className="text-center mb-6">
            <div className="text-3xl font-bold text-slate-800">
              $4.99<span className="text-lg text-slate-500">/month</span>
            </div>
            <p className="text-slate-500 text-sm">Cancel anytime</p>
          </div>
          
          <button
            onClick={handleAddPayment}
            className="w-full py-4 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl font-semibold hover:from-teal-700 hover:to-emerald-700 transition-all flex items-center justify-center gap-2"
            data-testid="add-payment-btn"
          >
            <CreditCard className="w-5 h-5" />
            Add Payment Method
          </button>
          
          <button
            onClick={handleLogout}
            className="w-full mt-3 py-3 border border-slate-300 text-slate-600 rounded-xl font-medium hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Log Out
          </button>
          
          <p className="text-center text-xs text-slate-500 mt-4">
            Your account will remain active once payment is added. No data will be lost.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentRequiredModal;
